import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { ExtractQualityAssurance } from "@emisgroup/cohort-builder";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ArtefactMode,
  ArtefactTypes,
  DeleteArtefact,
  Environments,
  ExportDialog,
  SelectedArtefactType,
  TriggerAccordionClick,
  TriggerClosePartition
} from "@emisgroup/cohort-builder-kit";
import { IRootState } from "../../../reducer/CombineReducer";
import { setNotification } from "../../../utilities/HttpRequest/Script";
import { getExtractListBasedOnSearchInput } from "../../Common/TreeList/Helper/Script";
import { ExtractAddSlice } from "../../LeftPanel/ExtractAdd";
import { ExtractModes, IExtract } from "../../LeftPanel/ExtractAdd/redux/Types";
import { IsInvalidateTitle } from "../../LeftPanel/ExtractAdd/script";
import { ExtractListSlice } from "../../LeftPanel/ExtractList";
import { sortFunction } from "../../LeftPanel/ExtractList/Helper/helper";
import { IExtractList } from "../../LeftPanel/ExtractList/redux/Types";
import { FinancialSlice } from "../../LeftPanel/FinancialYearList";
import { NotificationType } from "../../Notification/redux/Types";
import { createNotificationObject } from "../../Notification/Scripts";
import { ProgressSpinnerSlice } from "../../ProgressSpinner/Index";
import { ScheduleList } from "../SchedulingConfiguration/SchedulingList/SchedulingList.logic";
import { ExtractVersions } from "../Versioning/GetVersions/ExtractVersions.view";
import { OutputConfigLandingPage } from "../OutputConfig/OutputConfigLandingPage/OutputConfigLandingPage.logic";
import { handleException } from "../../../ErrorMessages/helper";
import { ErrorAlertSlice } from "../../ErrorAlert/Index";
import { ErrorAlertType, IErrorAlertObject } from "../../ErrorAlert/redux/Types";
import { ButtonVariantType } from "../../Common/UI/Types";
import { useUser } from "../../../AuthContext";
import { GetSelectedExtractDetail } from "../Versioning/GetVersions/helper";
import { OptOutSaveButton } from "../OptOut/OptOutSaveButton";
import { PartitionListSlice } from "../../LeftPanel/Partition";
import { BreadcrumbValue, BreadcrumbValueOptions } from "../BreadcrumbsContainer/redux/Types";
import { BreadcrumbsItemsSlice } from "../BreadcrumbsContainer";
import { ResultDetail } from "../Results/ResultDetail/ResultDetail";
import ExtractDetailView from "./ExtractDetail.view";
import { Navigation } from "./ExtractNavigation/Types";
import ExtractNavigationWithTabsList from "./ExtractNavigation/ExtractNavigation.logic";
import { StatusTypes } from "./Constant";
import { getSelectedMenu, updateStoredTabs } from "./Helper";

/**
 * Responsible for rendering the progress indicator component
 */
const ExtractDetail: React.FC = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [extractDetails, setExtractDetails] = useState({} as IExtractList);
  const [menu, setMenu] = useState(Navigation.DETAILS);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
  const [selectedDetailsTabId, setSelectedDetailsTabId] = useState<string>("");
  const [
    extractState,
    partition,
    extractAdd,
    breadcrumbsItems
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.partition,
    state.extractAdd,
    state.breadcrumbsItems
  ], shallowEqual);

  const extractData = useSelector((state: IRootState) => state.extractState.extractData);
  const searchInput = useSelector((state: IRootState) => state.extractState.searchInput);
  const currentExtractList = useSelector((state: IRootState) => state.extractState.extractList);

  const extractListFromData = useMemo(() => {
    return extractData?.data?.attributes?.extractList;
  }, [extractData]);

  const hasSetEmptyList = useRef(false);

  const [dataToPass, setDataToPass] = useState<BreadcrumbValue>(breadcrumbsItems?.breadcrumbSelectedItem);
  const { artefacts } = partition;
  const { selectedExtractGuid } = extractState;
  const currentExtract = extractState.selectedExtract;
  const isDetailsUpdated = extractAdd.isDetailsUpdated;
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [shouldNavigateToExecutionResults, setShouldNavigateToExecutionResults] = useState(false);

  const notification = createNotificationObject(
    "create-extract",
    `${translate("Notification.UpdateExtractSuccess")}`,
    "success");
  const handleExtractNavigation = (selectedMenu: Navigation): void => {
    setMenu(selectedMenu);
    setShouldNavigateToExecutionResults(false);
    updateStoredTabs(selectedExtractGuid, selectedMenu);
  };
  const { user } = useUser();

  /**
   * To get the selected details tab id
   * @param ev - Custom event to get the selected details tab id
   */
  const updateSelectedDetailsTabId = (ev: CustomEventInit): void => {
    if (ev.detail) {
      setSelectedDetailsTabId(ev.detail.artefactId);
    }
  };

  useEffect(() => {
    window.addEventListener("getExtractDetailsTabId", updateSelectedDetailsTabId);
    return (): void => {
      window.removeEventListener("getExtractDetailsTabId", updateSelectedDetailsTabId);
    };
  }, []);

  useEffect(() => {
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.VIEW));
    async function fetchData(): Promise<void> {
      const selectedExtractDetails = await GetSelectedExtractDetail(
        dispatch,
        selectedDetailsTabId || selectedExtractGuid,
        translate,
        user?.token
      );
      setExtractDetails(selectedExtractDetails);
    }
    if (selectedDetailsTabId || selectedExtractGuid) {
      fetchData();
    }
  }, [selectedExtractGuid, isDetailsUpdated, selectedDetailsTabId]);

  useEffect(() => {
    if (isDetailsUpdated) {
      const updatedArtefacts = artefacts?.map((item: SelectedArtefactType) => {
        if (item.id === selectedExtractGuid) {
          return {
            ...item,
            name: extractAdd?.newExtract?.title?.value
          };
        }
        return item;
      });
      dispatch(PartitionListSlice.actions.setPartitionList(updatedArtefacts));
      dispatch(ExtractAddSlice.actions.setDetailsUpdated(false));
    }
  }, [isDetailsUpdated]);

  /**
   * Dispatches the view extract to editExtract state on clicking
   * the edit button and sets the mode to edit
   */

  const onEditClick = (): void => {
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.EDIT));
    const name = currentExtract?.extractName as string;
    const version = currentExtract?.extractSchemaVersion as string;
    const survey = currentExtract?.surveyName as string;

    const { isInvalid: titleValid, validationText: titleTxt } = IsInvalidateTitle(name, "title", translate);
    const { isInvalid: verisonValid, validationText: versionTxt } = IsInvalidateTitle(version, "schema", translate);
    const { isInvalid: surveyValid, validationText: surveyTxt } = IsInvalidateTitle(survey, "survey", translate);

    const editData: IExtract = {
      title: {
        "value": name?.trim(),
        "valid": titleValid,
        "errorText": titleTxt
      },
      schema: {
        "value": version?.trim(),
        "valid": verisonValid,
        "errorText": versionTxt
      },
      survey: {
        "value": survey?.trim(),
        "valid": surveyValid,
        "errorText": surveyTxt
      },
      isActive: currentExtract?.isActive as boolean,
      description: currentExtract?.description,
      financialYear: currentExtract?.financialYear as string,
      extractGuid: currentExtract?.extractGuid as string
    };
    dispatch(ExtractAddSlice.actions.setEditExtractDetail(editData));
    dispatch(ExtractAddSlice.actions.setPreviousEditExtractDetail(editData));
  };

  /**
   * Method onclicking the delete icon
   */
  const onDeleteIconClick = (): void => {
    setShowAlert(!showAlert);
    const errorContent: IErrorAlertObject = {
      title: `${translate("Extract.DeleteExtract.Title")}`,
      message: [translate("Extract.DeleteExtract.FirstMessage"),
        extractState.selectedExtract?.extractName + "?",
        translate("Extract.DeleteExtract.SecondMessage")],
      type: ErrorAlertType.WARNING,
      actionButtonText: translate("Button.Delete"),
      cancelButtonText: translate("Button.Cancel"),
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      actionClick: () => onDeleteClick(),
      buttonVarient: ButtonVariantType.Danger
    };
    dispatch(ErrorAlertSlice.actions.createErrorAlert(errorContent));
  };

  /**
   * To remove the deleted partition from the partition list
   * @param partitionId - partition id to be removed
   */
  const removeDeletedPartition = (partitionId: string): void => {
    const removeDeletedPartition = artefacts.filter((item: SelectedArtefactType) => item.id !== partitionId);
    dispatch(PartitionListSlice.actions.setPartitionList(removeDeletedPartition));
    TriggerClosePartition(partitionId);
    const filteredPartitionList = removeDeletedPartition.filter((x: SelectedArtefactType) => x.level === 1);
    if (filteredPartitionList?.length > 0) {
      dispatch(PartitionListSlice.actions.setSelectedId(filteredPartitionList?.[0]?.id));
      TriggerAccordionClick(
        filteredPartitionList?.[0]?.id as string,
        true,
        ArtefactTypes.PARTITION,
        filteredPartitionList?.[0]?.name as string
      );
    } else {
      dispatch(PartitionListSlice.actions.setSelectedId(""));
    }
  };

  /**
   * API call for deleting the extract
   */
  const onDeleteClick = async (): Promise<void> => {
    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.DeleteExtract")));
      const response = await DeleteArtefact(
        selectedExtractGuid,
        process.env.APP_TARGET_ENV as Environments,
        user?.token,
        true
      );
      if (response && response["data"] && response["data"]["message"] === StatusTypes.DELETED_SUCCESS) {
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
        setNotification(
          dispatch,
          "delete-extract",
          translate("Notification.ExtractDelete"),
          NotificationType.Confirmation
        );
        dispatch(PartitionListSlice.actions.setSelectedId(""));
        dispatch(ExtractListSlice.actions.setSelectedExtractGuid(""));
        dispatch(ExtractListSlice.actions.selectedExtract(null));
        removeDeletedPartition(selectedExtractGuid);
      }
    } catch (ex) {
      handleException(ex, dispatch, "DeleteExtractErrMsg");
    }
    dispatch(ErrorAlertSlice.actions.showAlert(false));
  };

  React.useEffect(() => {
    if (!extractListFromData) {
      // Guard to prevent infinite loop: Only dispatch setExtractList([]) once when
      // ExtractListFromData becomes null/undefined. Without this check, clearing the list
      // Would trigger a re-render, which would dispatch again, causing an infinite loop.
      // The ref ensures we only clear the list once per "no data" state, and reset when data returns.
      if (!hasSetEmptyList.current && currentExtractList.length > 0) {
        hasSetEmptyList.current = true;
        dispatch(ExtractListSlice.actions.setExtractList([]));
      }
      return;
    }

    hasSetEmptyList.current = false;

    const searchtext = searchInput?.trim()?.toLowerCase();
    const sortedData = sortFunction([...extractListFromData]);

    const filteredList = searchtext && extractListFromData.length > 0
      ? getExtractListBasedOnSearchInput(searchtext, extractListFromData)
      : sortedData;

    dispatch(ExtractListSlice.actions.setExtractList(filteredList));
  }, [extractListFromData, searchInput, dispatch, currentExtractList]);

  // Set the item selected from breadcrumbs
  useEffect(() => {
    const selectedItemMap = {
      [BreadcrumbValueOptions.EXECUTION]: BreadcrumbValueOptions.EXECUTION,
      [BreadcrumbValueOptions.REPORTS]: BreadcrumbValueOptions.REPORTS,
      [BreadcrumbValueOptions.VIEWREPORT]: BreadcrumbValueOptions.VIEWREPORT,
    };

    const selectedValue = breadcrumbsItems?.breadcrumbSelectedItem;
    if (selectedValue && Object.prototype.hasOwnProperty.call(selectedItemMap, selectedValue)) {
      setDataToPass(selectedValue);
    }
  }, [breadcrumbsItems?.breadcrumbSelectedItem]);

  /**
   * Sets the view mode on clicking the cancel button
   */
  const onCancelClick = (): void => {
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.VIEW));
    dispatch(FinancialSlice.actions.selectedYear(extractState.selectedExtract?.financialYear as string));
  };

  /**
   * Method to show/hide export dialog
   */
  const handleExportDialogOpen = (): void => {
    setIsExportDialogOpen(!isExportDialogOpen);
  };

  /**
   * Responsible updating selected item to the store
   * @param item selected item
   */
  const handleItemClick = (item: BreadcrumbValue): void => {
    if (breadcrumbsItems?.clickedItem !== item) {
      dispatch(BreadcrumbsItemsSlice.actions.setBreadcrumbItem(undefined));
      dispatch(BreadcrumbsItemsSlice.actions.updateItemView(undefined));
      setDataToPass(undefined);
    }
    dispatch(BreadcrumbsItemsSlice.actions.setBreadcrumbItem(item));
  };

  useEffect(()=> {
    setMenu(getSelectedMenu(selectedExtractGuid));
  }, [selectedExtractGuid]);

  return (
    <>
      <ExtractNavigationWithTabsList menu={menu} menuSelectHandle={handleExtractNavigation} />
      <div>
        {menu === Navigation.DETAILS && selectedExtractGuid &&
          <>
            <ExtractDetailView
              selectedExtract={extractDetails}
              onEditClick={onEditClick}
              onCancelClick={onCancelClick}
              onDeleteIconClick={onDeleteIconClick}
              onDeleteClick={onDeleteClick}
              showAlert={showAlert}
              notification={notification}
              handleExportDialogOpen={handleExportDialogOpen}
            />
            {isExportDialogOpen &&
              <ExportDialog
                accessToken={user?.token}
                appMode={ArtefactMode.ASSESSMENT}
                artefactId={selectedExtractGuid}
                env={process.env.APP_TARGET_ENV as Environments}
                isOpen={isExportDialogOpen}
                name={extractDetails.extractName}
                setIsOpen={handleExportDialogOpen}
              />}
          </>
        }
        {menu === Navigation.SCHEDULING && <ScheduleList />}
        {menu === Navigation.VERSIONING && <ExtractVersions />}
        {menu === Navigation.QA &&
          <ExtractQualityAssurance
            selectedExtract={currentExtract as IExtractList}
            handleItemClick={handleItemClick}
            breadcrumbClickedItem={dataToPass}
            shouldNavigateToExecutionResults={shouldNavigateToExecutionResults}
          />}
        {menu === Navigation.OPTOUT && <OptOutSaveButton />}
        {menu === Navigation.OUTPUT && <OutputConfigLandingPage />}
        {menu === Navigation.RESULTS && <ResultDetail />}
      </div>
    </>
  );
};

export default ExtractDetail;
